import CryptoJS from 'crypto-js';
import BROCHURE_EMAIL_KEY from 'consts/storage-keys';

/**
 * FormTracker - Monitors and intercepts ClickDimension form submissions
 *
 * @description
 * - One delegated submit handler on document, registered through ClickDimensions' own jQuery,
 *   covers every ClickDimensions form, including ones injected later (e.g. in modals), with no
 *   polling or DOM observing.
 * - Extracts form data (name, email, phone) and hashes with SHA256
 * - Stores hashed user data in localStorage with form metadata
 * - Submits form to ClickDimension after 1.5s delay
 * - Pushes 'custom.form.complete' to the GTM dataLayer on the thank you page or
 *   'Brochure Sent!' modal, then clears stored data
 */

const STORAGE_KEY = 'formSubmissionTracker';

const releasedForms = new WeakSet(); // stored and being resubmitted, so let them through
const listeningJqueries = new WeakSet();

const hash = value => (value ? CryptoJS.SHA256(value).toString() : ''); // never hash an empty string

const getFormName = form => {
    const formTitle = form.parentElement?.parentElement?.querySelector('title')?.textContent || '';
    return formTitle.toLowerCase().includes('download') ? 'download brochure' : 'enquiry form';
};

const getFormType = () => (window.location.pathname.includes('degree-programs') ? 'dp' : 'sc');

const getUserData = form => {
    const data = Object.fromEntries(new FormData(form));
    const value = selector => data[form.querySelector(selector)?.name] || '';
    return {
        email: hash(value('input[aria-label="Email"]')),
        phone_number: hash(value('input[aria-label*="Phone"]')),
        first_name: hash(value('input[aria-label="First name"]')),
        last_name: hash(value('input[aria-label="Last name"]'))
    };
};

// Holds the submit for 1.5s so the hashed data is stored before ClickDimensions navigates away
const onFormSubmit = (cdJquery, form, event) => {
    if (releasedForms.has(form)) return; // our own resubmit below
    event.preventDefault();
    releasedForms.add(form);

    const savedData = {
        form: getFormName(form),
        type: getFormType(),
        user_data: getUserData(form)
    };

    // Storage can be blocked or full. Lose the tracking, never the enquiry: always submit.
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ savedData, timestamp: new Date().toISOString() }));
    } catch (error) {
        console.error('FormTracker: Failed to store formSubmissionTracker in localStorage', error);
    }
    setTimeout(() => cdJquery(form).submit(), 1500);
};

// ClickDimensions' SendForm() submits with clickd_jquery(form).submit(). That runs jQuery
// handlers, delegated ones included, then calls the native form.submit(), which fires no DOM
// event. So the handler must be a jQuery one, on ClickDimensions' own jQuery instance.
// A delegated handler also sees native submit events, and forms injected at any time.
const listen = () => {
    const cdJquery = window.clickd_jquery;
    if (!cdJquery || listeningJqueries.has(cdJquery)) return;
    listeningJqueries.add(cdJquery);
    cdJquery(document).on('submit', '.clickdform form', function onSubmit(event) {
        onFormSubmit(cdJquery, this, event);
    });
};

// Pushes the stored submission on the thank-you page or the 'Brochure Sent!' modal
const sendDataToGTM = () => {
    const isThankYouPage = (document.querySelector('title')?.textContent || '').toLowerCase().includes('thank you');
    const hasBrochureSentModal = !!document.querySelector('#params-trigger-modal h3')?.innerHTML?.toLowerCase().includes('brochure sent');
    if (!isThankYouPage && !hasBrochureSentModal) return;

    let stored;
    try {
        stored = localStorage.getItem(STORAGE_KEY);
        if (stored) localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        return; // storage blocked, so nothing can have been stored
    }
    if (!stored) return;

    let savedData;
    try {
        ({ savedData } = JSON.parse(stored) || {});
    } catch (error) {
        console.error('FormTracker: Failed to parse formSubmissionTracker from localStorage', error);
        return;
    }
    if (!savedData) return;

    let courseCode = '';
    try {
        courseCode = sessionStorage.getItem(savedData.type === 'sc' ? 'short-course' : 'degree-program') || '';
    } catch (error) {
        courseCode = '';
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'custom.form.complete',
        form: savedData.form,
        action: 'form_complete',
        type: savedData.type,
        course_code: courseCode,
        user_data: savedData.user_data
    });
};

// D365 forms: keep the email for the 'Brochure Sent!' modal's message, so it never has to go in
// the redirect URL. Read it on submit, as D365 may clear the fields before afterformsubmit, and
// store it only once the submit has succeeded. Neither event bubbles, so listen in capture.
let d365Email = '';
document.addEventListener('d365mkt-formsubmit', ({ target }) => {
    d365Email = (target.querySelector?.('input[name="emailaddress1"]')?.value || '').trim();
}, true);
document.addEventListener('d365mkt-afterformsubmit', ({ detail }) => {
    const email = d365Email;
    d365Email = '';
    if (!email || detail?.successful === false) return;
    try {
        sessionStorage.setItem(BROCHURE_EMAIL_KEY, email);
    } catch (error) {
        // Storage blocked: the modal keeps its CMS copy
    }
}, true);

// clickd_jquery can arrive after this script, even after load. Any click or submit inside a
// ClickDimensions form makes sure the handler is in place first: capture listeners run before
// the submit button's inline onclick="SendForm(...)".
listen();
['click', 'submit'].forEach(type => document.addEventListener(type, ({ target }) => {
    if (target instanceof Element && target.closest('.clickdform')) listen();
}, true));
document.addEventListener('DOMContentLoaded', listen);

window.addEventListener('load', () => {
    listen();
    sendDataToGTM();
});
